import { ApplicationConfig, provideZoneChangeDetection } from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideHttpClient, withFetch } from '@angular/common/http';
import { provideTranslateService } from '@ngx-translate/core';

import { routes } from './app.routes';
import { provideClientHydration, withNoHttpTransferCache } from '@angular/platform-browser';
import { SEARCH_PARSER } from './shared/services/search-parser.token';
import { DeterministicSearchParserService } from './shared/services/deterministic-search-parser.service';

/**
 * Root application providers (browser bootstrap).
 *
 * - `provideZoneChangeDetection({ eventCoalescing: true })` — batches change
 *   detection across grouped DOM events to cut redundant CD cycles.
 * - `provideRouter(routes)` — installs the lazy-loaded route tree.
 * - `provideClientHydration(withNoHttpTransferCache())` — reuses the SSR-rendered
 *   DOM on the client instead of re-rendering, avoiding a flash and preserving
 *   server markup. The HTTP transfer cache (on by default) is explicitly disabled:
 *   Angular 19 keys it with a weak 32-bit hash, so colliding requests can serve one
 *   user's SSR-cached response to another (GHSA-39pv-4j6c-2g6v). Every 19.x release
 *   is affected, including the final 19.2.25, so it cannot be patched on this major —
 *   disabling the cache is the only mitigation until the Angular 20 LTS upgrade.
 *   Dropping the cache costs nothing today: server-side requests carry no credentials
 *   (`UserService` gates token storage on `isPlatformBrowser`; `server.ts` forwards no
 *   cookies), so the dashboards' unguarded `ngOnInit` fetches already fail during SSR
 *   and cache nothing. Do not remove `withNoHttpTransferCache()` while on Angular 19.
 * - `provideHttpClient(withFetch())` — Fetch API backend, required for correct
 *   streaming/hydration under SSR. No HTTP interceptor: a server `401` just fails
 *   the API call (the previous dev-only session-expiry interceptor that bounced
 *   401s to /token-login was removed, so a backend 401 — e.g. from the legacy
 *   Compliance host — no longer navigates the app away).
 * - `provideTranslateService()` — registers `@ngx-translate` the standalone way
 *   (no NgModule). No loader is configured yet, so `instant()`/`get()` return the
 *   key until translation files are wired in.
 */
export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    provideClientHydration(withNoHttpTransferCache()),
    provideHttpClient(withFetch()),
    provideTranslateService(),
    { provide: SEARCH_PARSER, useClass: DeterministicSearchParserService },
  ],
};
